import { FlexCenter, FlexColumn } from '@elementary/common-ui/components';
import FormControls from '@elementary/common-ui/components/FormControls';
import { CircularProgress, styled } from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { DATA_HEALTH_TOTAL_FORMULA } from '@elementary/common-ui/constants/queryKeys';
import { Controller, useForm } from 'react-hook-form';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import { parseIntegrationError } from 'settings/utils';
import * as api from '../../services/api';
import { ScoreFormulaForm } from '../../types';
import DataHealthDialog from '../shared/DataHealthDialog';
import ScoreThresholdsSlider from '../shared/ScoreThresholdsSlider';
import DimensionRow from './DimensionRow';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
};

const CenteringWrapper = styled(FlexCenter)`
  height: 100%;
`;

const isTotalEquals100 = (weights: Record<string, number>) =>
  weights
    ? Object.values(weights).reduce(
        (sum, weight) => sum + Number(weight),
        0,
      ) === 100
    : false;

const ScoreFormulaDialog = ({ isOpen, onClose, onSave }: Props) => {
  const selectedEnvId = useSelectedEnv();

  const { data, isFetching, isError } = useQuery(
    [DATA_HEALTH_TOTAL_FORMULA],
    () => api.fetchDataHealthTotalFormula(selectedEnvId!),
    { staleTime: 0 },
  );

  const {
    mutate,
    isError: updateDimensionFormulaError,
    isLoading: isMutateLoading,
  } = useMutation(
    (scoreFormula: ScoreFormulaForm) =>
      api.updateDimensionFormula(
        selectedEnvId!,
        scoreFormula.weights,
        Math.min(...scoreFormula.sliderValues),
        Math.max(...scoreFormula.sliderValues),
      ),
    {
      onSuccess: () => {
        onSave();
      },
      onError: () => {
        onClose();
      },
    },
  );

  const {
    control,
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ScoreFormulaForm>({
    values: {
      sliderValues: data ? [data.thresholds.low, data.thresholds.medium] : [],
      weights: data?.weights || {},
    },
    mode: 'onBlur',
  });

  return (
    <DataHealthDialog
      title="Configure Data Health Formulas"
      description="Set your dimensions weight"
      width="586px"
      open={isOpen}
      onClose={onClose}
      onSubmit={handleSubmit((formData: ScoreFormulaForm) => mutate(formData))}
      loading={isMutateLoading}
      error={
        parseIntegrationError(errors) ||
        parseIntegrationError(updateDimensionFormulaError)
      }
      isValid={!isError && isTotalEquals100(watch('weights'))}
    >
      {isFetching === true && (
        <CenteringWrapper>
          <CircularProgress />
        </CenteringWrapper>
      )}

      {isFetching === false && data !== undefined && (
        <FlexColumn gap={3}>
          <Controller
            render={({ field }) => (
              <ScoreThresholdsSlider
                sliderValues={field.value}
                onChange={field.onChange}
              />
            )}
            name="sliderValues"
            control={control}
          />
          {Object.entries(data.weights).map(([key]) => (
            <FlexColumn key={key}>
              <FormControls.InputWrapper>
                <DimensionRow dimension={key} register={register} />
                <FormControls.ErrorMessage sx={{ top: '48px' }}>
                  {errors.weights?.[key]?.message}
                </FormControls.ErrorMessage>
              </FormControls.InputWrapper>
            </FlexColumn>
          ))}
          <FormControls.InputWrapper>
            <FormControls.ErrorMessage sx={{ top: '0px' }}>
              {!isTotalEquals100(watch('weights')) &&
                'The sum of all weights should be 100%'}
            </FormControls.ErrorMessage>
          </FormControls.InputWrapper>
        </FlexColumn>
      )}
    </DataHealthDialog>
  );
};

export default ScoreFormulaDialog;
